// Helper function to calculate days worked (including weekends)
export const calculateDaysWorked = (joiningDate) => {
  if (!joiningDate) return 0;
  
  const joinDate = new Date(joiningDate);
  const currentDate = new Date();
  
  // Check if join date is valid
  if (isNaN(joinDate.getTime())) return 0;
  
  // If join date is in the future, return 0
  if (joinDate > currentDate) return 0;
  
  // Calculate difference in milliseconds
  const diffTime = Math.abs(currentDate - joinDate);
  
  // Convert to days (including all calendar days - weekends included)
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

// Helper function to calculate months worked (including partial months)
export const calculateMonthsWorked = (joiningDate) => {
  if (!joiningDate) return 0;
  
  const joinDate = new Date(joiningDate);
  const currentDate = new Date();
  
  // Check if join date is valid
  if (isNaN(joinDate.getTime())) return 0;
  
  // If join date is in the future, return 0
  if (joinDate > currentDate) return 0;
  
  // Calculate year and month differences
  let years = currentDate.getFullYear() - joinDate.getFullYear();
  let months = currentDate.getMonth() - joinDate.getMonth();
  let days = currentDate.getDate() - joinDate.getDate();
  
  // Adjust for negative month difference
  if (months < 0) {
    years--;
    months += 12;
  }
  
  // Adjust for negative day difference (partial month)
  if (days < 0) {
    months--;
    if (months < 0) {
      years--;
      months += 12;
    }
    // Add the fractional part for the incomplete month
    const daysInPreviousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
    days += daysInPreviousMonth;
  }
  
  // Total months
  const totalMonths = years * 12 + months;
  
  // Add partial month (as decimal) if there are remaining days
  const partialMonth = days / 30; // Using 30 as average month length
  
  return Math.max(0, totalMonths + partialMonth);
};

// Helper function to format months worked for display
export const formatMonthsWorked = (joiningDate) => {
  const totalMonths = calculateMonthsWorked(joiningDate);
  
  if (totalMonths < 1) {
    const days = calculateDaysWorked(joiningDate);
    return `${days} days`;
  }
  
  const years = Math.floor(totalMonths / 12);
  const months = Math.floor(totalMonths % 12);
  const decimalPart = totalMonths % 1;
  const days = Math.round(decimalPart * 30);
  
  let result = [];
  
  if (years > 0) {
    result.push(`${years} year${years !== 1 ? 's' : ''}`);
  }
  
  if (months > 0) {
    result.push(`${months} month${months !== 1 ? 's' : ''}`);
  }
  
  if (days > 0 && totalMonths < 12) { // Only show days for less than a year
    result.push(`${days} day${days !== 1 ? 's' : ''}`);
  }
  
  return result.join(', ') || '0 days';
};

// Alternative simple calculation for total months (rounds to nearest integer)
export const calculateTotalMonths = (joiningDate) => {
  if (!joiningDate) return 0;
  
  const joinDate = new Date(joiningDate);
  const currentDate = new Date();
  
  // Check if join date is valid
  if (isNaN(joinDate.getTime())) return 0;
  
  // If join date is in the future, return 0
  if (joinDate > currentDate) return 0;
  
  // Simple calculation: difference in days divided by 30.44 (average days per month)
  const diffDays = calculateDaysWorked(joiningDate);
  const months = Math.round(diffDays / 30.44);
  
  return Math.max(1, months); // At least 1 month if employee has joined
};

// Helper to get exact month difference (for precise calculations)
export const getExactMonthDifference = (startDate, endDate = new Date()) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  if (start > end) return 0;
  
  let months = (end.getFullYear() - start.getFullYear()) * 12;
  months -= start.getMonth();
  months += end.getMonth();
  
  // Add fractional month based on day of month
  const startDay = start.getDate();
  const endDay = end.getDate();
  const daysInEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  
  if (endDay >= startDay) {
    // Full month plus some days
    months += (endDay - startDay) / daysInEndMonth;
  } else {
    // Not a full month
    months -= 1;
    const daysInPrevMonth = new Date(end.getFullYear(), end.getMonth(), 0).getDate();
    months += (daysInPrevMonth - startDay + endDay) / daysInPrevMonth;
  }
  
  return Math.max(0, months);
};